/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import { useState, useEffect, useCallback } from "react";
import {
  addToCart as addToCartAPI,
  getCart as getCartAPI,
  removeFromCart as removeFromCartAPI,
  getCurrentCustomerId,
  isCustomerLoggedIn,
} from "../utils/storeApi";
import { buildImageUrl } from "../theme1/utils/theme1Helpers";

const guestCartKey = (tenantId) => `storeGuestCart_${tenantId}`;

const readGuestCart = (tenantId) => {
  try {
    const raw = localStorage.getItem(guestCartKey(tenantId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeGuestCart = (tenantId, items) => {
  localStorage.setItem(guestCartKey(tenantId), JSON.stringify(items || []));
};

const findStoreProduct = (itemData) => {
  const allProducts = window.__STORE_PRODUCTS__ || [];
  const itemId = Number(itemData?.itemId || itemData?.id || 0);
  const variantId = Number(itemData?.variantId || 0);
  return allProducts.find((p) => {
    if (itemId && Number(p.id) === itemId) return true;
    return (p.variants || []).some((v) => Number(v.variantId) === variantId);
  });
};

const mapGuestItem = (itemData, qty) => {
  const product = findStoreProduct(itemData);
  const variant = (product?.variants || []).find(
    (v) => Number(v.variantId) === Number(itemData.variantId)
  );
  const image =
    itemData.imageUrl ||
    itemData.image ||
    itemData.mainImage ||
    itemData.mainImageUrl ||
    variant?.imageUrl ||
    product?.mainImage ||
    product?.mainImageUrl ||
    "";
  const parentImage = product?.mainImage || product?.mainImageUrl || "";

  return {
    orderId: "",
    itemId: Number(itemData.itemId || itemData.id || product?.id || 0),
    variantId: Number(itemData.variantId),
    name: itemData.name || itemData.itemName || product?.name || "",
    color: itemData.color || itemData.colorHex || itemData.hex || "",
    categoryName: itemData.category || itemData.categoryName || product?.category || "",
    size: itemData.size || "",
    quantity: Number(qty || 1),
    price: Number(itemData.price || 0),
    stockQty: Number(itemData.stockQty ?? variant?.stockQty ?? 999),
    imageUrl: buildImageUrl(image),
    parentImageUrl: buildImageUrl(parentImage),
    costPrice: Number(itemData.costPrice || variant?.costPrice || 0),
    discount: Number(itemData.discount || 0),
  };
};

export function useSmartCart(tenantId) {
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);

  const persistGuest = useCallback((items) => {
    writeGuestCart(tenantId, items);
    setCart(items);
  }, [tenantId]);

  const clearCart = useCallback(() => {
    setCart([]);
    localStorage.removeItem("currentOrderId");
    localStorage.removeItem("storeCurrentOrder");
    if (tenantId) localStorage.removeItem(guestCartKey(tenantId));
  }, [tenantId]);

  const refreshCart = useCallback(async () => {
    if (!tenantId) {
      setCart([]);
      return;
    }

    if (!isCustomerLoggedIn()) {
      setCart(readGuestCart(tenantId));
      return;
    }

    try {
      setLoading(true);
      const res = await getCartAPI(tenantId, getCurrentCustomerId());

      if (!res?.cartData?.items) {
        setCart([]);
        return;
      }

      const cartOrderId = String(
        res.cartData?.cart?.OrderId ||
        res.cartData?.cart?.orderId ||
        res.cartData?.OrderId ||
        res.cartData?.orderId ||
        ""
      ).trim();
      if (cartOrderId) {
        localStorage.setItem("currentOrderId", cartOrderId);
      }

      const allProducts = window.__STORE_PRODUCTS__ || [];

      const mapped = res.cartData.items.flatMap((item) =>
        (item.variants || []).map((v) => {
          let finalNetPrice = Number(v.price || 0);
          let originalHex = v.color;
          let currentStock = Number(v.stockQty || 0);
          let categoryName = item.categoryName || item.category || "";
          let costPrice = Number(v.costPrice || 0);

          const productMatch = allProducts.find((p) => Number(p.id) === Number(item.itemId));
          let parentImage = "";
          let variantImage = v.imageUrl;
          if (productMatch) {
            parentImage = productMatch.mainImage || productMatch.mainImageUrl || "";
            categoryName = productMatch.category || productMatch.categoryName || categoryName;
            const variantMatch = productMatch.variants?.find(
              (vr) => Number(vr.variantId) === Number(v.variantId)
            );
            if (variantMatch) {
              const basePrice = Number(variantMatch.price || 0);
              const discountAmount = Number(variantMatch.discount || 0);
              finalNetPrice = basePrice - discountAmount;
              originalHex = variantMatch.color?.hex || variantMatch.color?.hexCode || v.color;
              variantImage = variantMatch.imageUrl || v.imageUrl;
              costPrice = Number(variantMatch.costPrice || 0);

              const sizeMatch = variantMatch.options?.find((s) => s.label === v.size);
              if (sizeMatch) {
                currentStock = Number(sizeMatch.stock || 0);
              } else {
                currentStock = Number(variantMatch.stockQty || 0);
              }
            }
          }

          const rawImage =
            variantImage ||
            item.imageUrl ||
            item.mainImage ||
            item.mainImageUrl ||
            parentImage;

          return {
            orderId: cartOrderId,
            itemId: item.itemId,
            variantId: v.variantId,
            name: item.itemName,
            color: originalHex,
            categoryName,
            size: v.size,
            quantity: Number(v.quantity || 1),
            price: finalNetPrice,
            stockQty: currentStock,
            imageUrl: buildImageUrl(rawImage),
            parentImageUrl: buildImageUrl(parentImage),
            costPrice,
          };
        })
      );
      setCart(mapped);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    refreshCart();
  }, [refreshCart]);

  const addItem = useCallback(async (itemData, qtyToAdd = 1) => {
    if (!itemData?.variantId || !tenantId) return { success: false };

    if (itemData.stockQty !== undefined && qtyToAdd > itemData.stockQty) {
      return { success: false, error: "الكمية المطلوبة غير متوفرة في المخزون" };
    }

    if (!isCustomerLoggedIn()) {
      const current = readGuestCart(tenantId);
      const idx = current.findIndex(
        (x) =>
          Number(x.variantId) === Number(itemData.variantId) &&
          String(x.size || "") === String(itemData.size || "")
      );

      if (idx >= 0) {
        const nextQty = Number(current[idx].quantity || 0) + Number(qtyToAdd);
        if (nextQty > Number(current[idx].stockQty || itemData.stockQty || 999)) {
          return { success: false, error: "الكمية المطلوبة غير متوفرة في المخزون" };
        }
        current[idx] = { ...current[idx], quantity: nextQty };
      } else {
        current.push(mapGuestItem(itemData, qtyToAdd));
      }

      persistGuest(current);
      return { success: true };
    }

    const customerId = getCurrentCustomerId();
    if (!customerId) return { success: false };

    try {
      const res = await addToCartAPI(
        {
          variantId: itemData.variantId,
          price: Number(itemData.price),
          discount: Number(itemData.discount || 0),
          color: itemData.color,
          size: itemData.size || "",
        },
        qtyToAdd,
        tenantId,
        customerId
      );

      if (res?.success) {
        const createdOrderId = String(res.orderId || "").trim();
        if (createdOrderId) {
          localStorage.setItem("currentOrderId", createdOrderId);
        }
        await refreshCart();
        return { success: true };
      }
      return { success: false, error: res?.message || "فشل إضافة المنتج" };
    } catch {
      return { success: false, error: "حدث خطأ أثناء الإضافة للسلة" };
    }
  }, [tenantId, persistGuest, refreshCart]);

  const updateQuantity = useCallback(async (variantId, delta) => {
    const item = cart.find((x) => Number(x.variantId) === Number(variantId));
    if (!item) return { success: false };

    if (delta > 0 && item.quantity + delta > item.stockQty) {
      return { success: false, error: "لقد وصلت للحد الأقصى المتوفر في المخزون" };
    }

    if (!isCustomerLoggedIn()) {
      const current = readGuestCart(tenantId);
      const idx = current.findIndex((x) => Number(x.variantId) === Number(variantId));
      if (idx < 0) return { success: false };

      const nextQty = Number(current[idx].quantity || 1) + Number(delta);
      if (nextQty <= 0) {
        persistGuest(current.filter((x) => Number(x.variantId) !== Number(variantId)));
      } else {
        current[idx] = { ...current[idx], quantity: nextQty };
        persistGuest(current);
      }
      return { success: true };
    }

    const customerId = getCurrentCustomerId();
    const orderId = localStorage.getItem("currentOrderId");

    try {
      if (item.quantity === 1 && delta === -1) {
        await removeFromCartAPI(tenantId, orderId, variantId, customerId);
      } else {
        await addToCartAPI(
          {
            variantId,
            price: Number(item.price),
            discount: 0,
            color: item.color,
            size: item.size || "",
          },
          delta,
          tenantId,
          customerId
        );
      }

      await refreshCart();
      return { success: true };
    } catch {
      return { success: false, error: "حدث خطأ أثناء تحديث الكمية" };
    }
  }, [cart, tenantId, persistGuest, refreshCart]);

  const removeItem = useCallback(async (variantId) => {
    if (!isCustomerLoggedIn()) {
      persistGuest(
        readGuestCart(tenantId).filter((x) => Number(x.variantId) !== Number(variantId))
      );
      return { success: true };
    }

    const customerId = getCurrentCustomerId();
    const orderId = localStorage.getItem("currentOrderId");

    try {
      await removeFromCartAPI(tenantId, orderId, variantId, customerId);
      await refreshCart();
      return { success: true };
    } catch {
      return { success: false };
    }
  }, [tenantId, persistGuest, refreshCart]);

  const mergeGuestCartToAccount = useCallback(async () => {
    if (!tenantId || !isCustomerLoggedIn()) return;
    const guestItems = readGuestCart(tenantId);
    if (!guestItems.length) {
      await refreshCart();
      return;
    }

    localStorage.removeItem(guestCartKey(tenantId));
    const customerId = getCurrentCustomerId();

    for (const item of guestItems) {
      try {
        await addToCartAPI(
          {
            variantId: item.variantId,
            price: Number(item.price),
            discount: Number(item.discount || 0),
            color: item.color,
            size: item.size || "",
          },
          Number(item.quantity || 1),
          tenantId,
          customerId
        );
      } catch {
        /* keep going so remaining guest items still sync */
      }
    }

    await refreshCart();
  }, [tenantId, refreshCart]);

  return {
    cart,
    loading,
    addItem,
    updateQuantity,
    removeItem,
    refreshCart,
    clearCart,
    mergeGuestCartToAccount,
  };
}
